import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { Filters, Status, SurveyI } from "@seneca/surveys-service-types";

import { FeatureFlagsResponse } from "services/featureFlagsService/FeatureFlagsServiceClient";

import { useSelectedSchool } from "seneca-common/features/schools/school-selection/hooks";
import { getMySubscriptionTierName } from "seneca-common/features/subscriptions/features/subscription-products/selectors/subscriptionSubscriptionProduct";
import {
  getLiveManagedSubscriptionId,
  getLiveStripeSubscriptionId,
  hasLiveSubscription,
  subscriptionsFetchFinished
} from "seneca-common/features/subscriptions/state";
import {
  isFirstUserSession as getIsFirstUserSession,
  getTeacherRole,
  getUserId,
  isSignedIn,
  isSignedInAsGuest,
  isUserManaged
} from "seneca-common/features/user/state";
import shuffle from "seneca-common/utils/functions/random/shuffle";
import { useSelectorOnCondition } from "seneca-common/utils/hooks/selectors";

import useAppVariant from "features/common/hooks/useAppVariant";
import { isMobile, isTablet } from "features/common/utils/functions/mobile";
import { useFetchFeatureFlags } from "features/feature-flags";
import { getRegion } from "features/regions/selectors";
import { logSurveyToastShown } from "features/surveys/analytics";
import useFetchAllActiveSurveys from "features/surveys/hooks/useFetchAllActiveSurveys";
import {
  hasAnySurveyBeenSeenRecently,
  hasSurveyBeenSeen,
  setSurveySeen
} from "features/surveys/state";
import { getUserAccountType } from "features/user/selectors";

import { canShowSurveyToast, showSurveyToast } from "./SurveyToast";

type FilterKey = keyof Filters;

type SurveyFilterData = {
  accountType?: string | null;
  region?: string | null;
  url?: string;
  schoolId?: string | null;
  teacherRole?: string | null;
  isPremiumUser?: boolean;
  productTier?: string | null;
  isManaged?: boolean;
  userId?: string | null;
  isFirstUserSession?: boolean;
  featureFlags?: FeatureFlagsResponse;
};

export default function useShowSurveyToast() {
  const { data: surveys } = useFetchAllActiveSurveys();
  const subscriptionsFetched = useSelector(subscriptionsFetchFinished);
  const userId = useSelector(getUserId);
  const accountType = useSelector(getUserAccountType);
  const location = useLocation();
  const region = useSelector(getRegion);
  const signedIn = useSelector(isSignedIn);
  const signedInAsGuest = useSelector(isSignedInAsGuest);
  const { data: schoolInfo } = useSelectedSchool({
    enabled: signedIn && !signedInAsGuest,
    select: school => school?.schoolInfo
  });
  const schoolId = schoolInfo?.schoolId;
  const teacherRole = useSelector(getTeacherRole);
  const isPremiumUser = useSelector(hasLiveSubscription);
  const subscriptionId = useSelectorOnCondition(
    subscriptionsFetched,
    getLiveStripeSubscriptionId
  );
  const managedSubscriptionId = useSelectorOnCondition(
    subscriptionsFetched,
    getLiveManagedSubscriptionId
  );
  const productTier = useSelectorOnCondition(
    subscriptionsFetched && (subscriptionId || managedSubscriptionId),
    getMySubscriptionTierName,
    subscriptionId || managedSubscriptionId
  );
  const isManaged = useSelector(isUserManaged);
  const isFirstUserSession = useSelector(getIsFirstUserSession);
  const enabled = useEnabledByVariant();
  const { data: featureFlags } = useFetchFeatureFlags({
    enabled: enabled && signedIn
  });

  useEffect(() => {
    if (
      enabled &&
      userId &&
      surveys &&
      subscriptionsFetched &&
      canShowSurveyToast()
    ) {
      const surveyToShow = getSurveyToShow(surveys, userId, {
        accountType,
        region,
        url: `${window.location.origin}${location.pathname}${location.search}`,
        schoolId,
        teacherRole,
        isPremiumUser,
        productTier,
        isManaged,
        userId,
        isFirstUserSession,
        featureFlags
      });

      if (surveyToShow) {
        const {
          surveyId,
          title,
          description,
          url,
          buttonText,
          showAfterMillis
        } = surveyToShow;
        showSurveyToast({
          surveyId,
          title,
          description,
          url,
          buttonText,
          showAfterMillis
        });
        setSurveySeen(surveyId, userId);
        logSurveyToastShown(surveyId);
      }
    }
  }, [
    enabled,
    surveys,
    subscriptionsFetched,
    accountType,
    region,
    location.pathname,
    location.search,
    userId,
    schoolId,
    teacherRole,
    isPremiumUser,
    productTier,
    isManaged,
    isFirstUserSession,
    featureFlags
  ]);
}

function getSurveyToShow(
  surveys: SurveyI[],
  userId: string,
  data: SurveyFilterData
) {
  if (hasAnySurveyBeenSeenRecently(userId)) {
    return undefined;
  }

  const validSurveys = surveys.filter(({ surveyId, status, filters }) => {
    if (hasSurveyBeenSeen(surveyId, userId) || status !== Status.active) {
      return false;
    }

    return Object.keys(filters).every(key =>
      evaluateFilter(filters, key as FilterKey, data)
    );
  });

  return shuffle(validSurveys)[0];
}

function evaluateFilter(
  filters: Filters,
  key: FilterKey,
  {
    region,
    url,
    accountType,
    teacherRole,
    isPremiumUser,
    productTier,
    isManaged,
    schoolId,
    userId,
    isFirstUserSession,
    featureFlags
  }: SurveyFilterData
) {
  const value = filters[key];

  if (value === undefined) {
    return true;
  }

  switch (key) {
    case "regions":
      return (value as string[]).includes(region || "");
    case "urlPaths":
      return (value as string[]).some(val => url?.includes(val));
    case "regexMatchUrlPaths":
      return (value as string[]).some(val => url && new RegExp(val).test(url));
    case "accountTypes":
      return (value as string[]).includes(accountType || "");
    case "schoolIds":
      return (value as string[]).includes(schoolId || "");
    case "teacherRoles":
      return (value as string[]).includes(teacherRole || "");
    case "isPremiumUser":
      return (value as boolean) === isPremiumUser;
    case "productTiers":
      return (value as string[]).includes(productTier || "");
    case "isManaged":
      return (value as boolean) === isManaged;
    case "deviceCategories":
      return evaluateDeviceCategoriesFilter(value as string[]);
    case "userIds":
      return (value as string[]).includes(userId || "");
    case "isFirstUserSession":
      return (value as boolean) === isFirstUserSession;
    case "featureFlag":
      const filter = value as Filters["featureFlag"];
      if (!featureFlags || !filter) return false;
      const featureFlagValue = featureFlags[filter.type]?.[filter.flagId].value;

      if (!featureFlagValue) return false;

      if (
        typeof filter.values === "boolean" ||
        typeof featureFlagValue === "boolean"
      ) {
        return filter.values === featureFlagValue;
      }

      return filter.values.includes(featureFlagValue);
    default:
      return false;
  }
}

const isDeviceFunctionByCategory = {
  mobile: isMobile,
  tablet: isTablet,
  desktop: () => !isMobile() && !isTablet()
};

function evaluateDeviceCategoriesFilter(filter: string[]) {
  return Object.entries(isDeviceFunctionByCategory).some(
    ([category, isDevice]) => filter.includes(category) && isDevice()
  );
}

function useEnabledByVariant() {
  const appVariant = useAppVariant();

  const valuesByVariant = {
    seneca: true,
    senecaPrimary: true,
    plurall: false,
    goStudentLearning: false
  };

  return valuesByVariant[appVariant];
}
